import React from 'react';
import { Button } from 'react-bootstrap';
import { InputText, NumericSelect } from '../../../../formikBindings';
import { FieldArray, useFormikContext } from 'formik';
import { ISiteVisitorTypeListProps } from './types';
import SiteVisitorTypeFactory from '../SiteVisitorTypeFactory';
import styles from './SiteVisitorTypeList.module.css';
import {
  FormControlErrorMessage,
  ISelectOption,
  SelectOptions,
} from '../../../../form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ISiteFormValues, ISiteVisitorType } from '../types';

const SiteVisitorTypeList: React.FC<ISiteVisitorTypeListProps> = ({
  items,
  visitorTypes,
  agreements,
  questionnaires,
}) => {
  const {
    values: { visitorTypes: currentVisitorTypes },
    setFieldValue,
  } = useFormikContext<ISiteFormValues>();

  const getFilteredVisitorTypes = (itemIndex: number): Array<ISelectOption> => {
    const results: Array<ISelectOption> = [];

    // https://stackoverflow.com/questions/43118692/typescript-filter-out-nulls-from-an-array
    let visitorTypeIdInUse: Array<string> = items.flatMap((item) =>
      !!item.visitorTypeId ? [item.visitorTypeId.toString()] : [],
    );

    if (items[itemIndex].visitorTypeId) {
      visitorTypeIdInUse = visitorTypeIdInUse.filter(
        (id) => id !== items[itemIndex].visitorTypeId!.toString(),
      );
    }

    for (let index = 0; index < visitorTypes.length; index++) {
      if (!visitorTypeIdInUse.includes(visitorTypes[index].value)) {
        results.push(visitorTypes[index]);
      }
    }

    return results;
  };

  const getSupplementaryQuestionnaires = (
    item: ISiteVisitorType,
  ): Array<ISelectOption> => {
    return questionnaires.filter(
      (questionnaire) =>
        questionnaire.value !== (item.questionnaireId ?? '').toString(),
    );
  };

  const handleQuestionnaireChanged = (value: any, itemIndex: number): void => {
    setFieldValue(`visitorTypes.${itemIndex}.questionnaireId`, value, true);

    if (!value) {
      setFieldValue(
        `visitorTypes.${itemIndex}.questionnaireEmailAddress`,
        '',
        true,
      );

      handleSupplementaryQuestionnaireChanged(null, itemIndex);
    } else if (
      value === currentVisitorTypes[itemIndex].supplementaryQuestionnaireId
    ) {
      handleSupplementaryQuestionnaireChanged(null, itemIndex);
    }
  };

  const handleSupplementaryQuestionnaireChanged = (
    value: any,
    itemIndex: number,
  ): void => {
    setFieldValue(
      `visitorTypes.${itemIndex}.supplementaryQuestionnaireId`,
      value,
      true,
    );

    if (!value) {
      setFieldValue(
        `visitorTypes.${itemIndex}.supplementaryQuestionnaireEmailAddress`,
        '',
        true,
      );
    }
  };

  return (
    <div className={styles.viewPort}>
      <FieldArray
        name="visitorTypes"
        validateOnChange={false}
        render={(itemsArrayHelper) => (
          <div className={styles.tableContainer}>
            <div className={`${styles.headerRow} ${styles.row}`}>
              <div className={`required ${styles.visitorTypeCol}`}>Type</div>
              <div className={styles.questionnaireCol}>Questionnaire</div>
              <div className={styles.questionnaireEmailCol}>
                Questionnaire Email
              </div>
              <div className={styles.questionnaireCol}>
                Supplementary Questionnaire
              </div>
              <div className={styles.questionnaireEmailCol}>
                Supplementary Questionnaire Email
              </div>
              <div className={styles.agreementCol}>Visitor Agreement</div>
              <div className={styles.buttonsColumn} />
              <div className={styles.buttonsColumn} />
            </div>
            {items.map((item: ISiteVisitorType, itemIndex: number) => (
              <div className={styles.row} key={itemIndex}>
                <div className={styles.visitorTypeCol}>
                  <NumericSelect
                    className={styles.selectErrorIcon}
                    name={`visitorTypes.${itemIndex}.visitorTypeId`}
                  >
                    <option value="" disabled>
                      Select visitor type..
                    </option>
                    <SelectOptions
                      options={getFilteredVisitorTypes(itemIndex)}
                    />
                  </NumericSelect>
                  <FormControlErrorMessage
                    name={`visitorTypes.${itemIndex}.visitorTypeId`}
                  />
                </div>

                <NumericSelect
                  className={`${styles.questionnaireCol} ${styles.selectErrorIcon}`}
                  name={`visitorTypes.${itemIndex}.questionnaireId`}
                  disabled={items[itemIndex].visitorTypeId === null}
                  onChange={(value) =>
                    handleQuestionnaireChanged(value, itemIndex)
                  }
                >
                  <option />
                  <SelectOptions options={questionnaires} />
                </NumericSelect>

                <div className={styles.questionnaireEmailCol}>
                  <InputText
                    name={`visitorTypes.${itemIndex}.questionnaireEmailAddress`}
                    maxLength={256}
                    disabled={
                      items[itemIndex].visitorTypeId === null ||
                      items[itemIndex].questionnaireId === null
                    }
                  />
                  <FormControlErrorMessage
                    name={`visitorTypes.${itemIndex}.questionnaireEmailAddress`}
                  />
                </div>

                <NumericSelect
                  className={`${styles.questionnaireCol} ${styles.selectErrorIcon}`}
                  name={`visitorTypes.${itemIndex}.supplementaryQuestionnaireId`}
                  disabled={
                    items[itemIndex].visitorTypeId === null ||
                    items[itemIndex].questionnaireId === null
                  }
                  onChange={(value) =>
                    handleSupplementaryQuestionnaireChanged(value, itemIndex)
                  }
                >
                  <option />
                  <SelectOptions
                    options={getSupplementaryQuestionnaires(item)}
                  />
                </NumericSelect>

                <div className={styles.questionnaireEmailCol}>
                  <InputText
                    name={`visitorTypes.${itemIndex}.supplementaryQuestionnaireEmailAddress`}
                    maxLength={256}
                    disabled={
                      items[itemIndex].visitorTypeId === null ||
                      items[itemIndex].supplementaryQuestionnaireId === null
                    }
                  />
                  <FormControlErrorMessage
                    name={`visitorTypes.${itemIndex}.supplementaryQuestionnaireEmailAddress`}
                  />
                </div>

                <NumericSelect
                  className={`${styles.agreementCol} ${styles.selectErrorIcon}`}
                  name={`visitorTypes.${itemIndex}.agreementId`}
                  disabled={items[itemIndex].visitorTypeId === null}
                >
                  <option />
                  <SelectOptions options={agreements} />
                </NumericSelect>

                <div className={styles.buttonsColumn}>
                  {items.length > 1 && (
                    <Button
                      variant="danger"
                      size="sm"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        itemsArrayHelper.remove(itemIndex);
                      }}
                    >
                      <FontAwesomeIcon icon="times" />
                    </Button>
                  )}
                  {itemIndex === items.length - 1 &&
                    items.length !== visitorTypes.length && (
                      <Button
                        variant="info"
                        size="sm"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          itemsArrayHelper.insert(
                            itemIndex + 1,
                            SiteVisitorTypeFactory.Create(),
                          );
                        }}
                      >
                        <FontAwesomeIcon icon="plus" />
                      </Button>
                    )}
                </div>
                <div className={styles.buttonsColumn}>
                  {itemIndex > 0 && (
                    <Button
                      size="sm"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        itemsArrayHelper.move(itemIndex, itemIndex - 1);
                      }}
                    >
                      <FontAwesomeIcon icon={['far', 'arrow-up']} />
                    </Button>
                  )}

                  {itemIndex < items.length - 1 && (
                    <Button
                      size="sm"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        itemsArrayHelper.move(itemIndex, itemIndex + 1);
                      }}
                    >
                      <FontAwesomeIcon icon={['far', 'arrow-down']} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      />
    </div>
  );
};

export default SiteVisitorTypeList;
